import React from 'react'

import {
  Box,
  BoxProps,
  Grid,
  GridProps,
  GridSpacing,
} from '@material-ui/core'

import {
  DateFilterComponent,
  DateFilterComponentProps,
  DateRangeFilterComponent,
  DateRangeFilterComponentProps,
  FilterComponentClassName,
  MultiOptionFilterComponentProps,
  MultiOptionGroupFilterComponent,
  SingleOptionFilterComponentProps,
  SingleOptionGroupFilterComponent,
} from './FilterComponents'

import { makeAppStyles } from '../../../themes'

import { TypedKeys } from '@percept/types'
import { ClassNameMap } from '@material-ui/core/styles/withStyles'



type NullableValues<T extends object> = {
  [K in keyof T]: T[K] | null 
}


type NonArrayObject = object & { [Symbol.iterator]?: never; }

export type InlineFilterComponentConfigItem<T extends object> = (
  (
    Omit<MultiOptionFilterComponentProps, 'value' | 'classes' | 'color'>
    & {
      type: 'MultiOptionGroup'
      value: unknown[]
      // itemKey: TypedKeys<T, Array<unknown>>
      key: string
    }
   ) | (
    Omit<SingleOptionFilterComponentProps, 'value' | 'classes' | 'color'>
    & {
      type: 'SingleOptionGroup'
      value: unknown
      // itemKey: TypedKeys<T, undefined | null | string | number | NonArrayObject>
      key: string
    }
   ) | (
    Omit<DateRangeFilterComponentProps, 'value' | 'classes' | 'color'>
    & {
      type: 'DateRange'
      value: [Date, Date]
      // itemKey: TypedKeys<T, [Date | null, Date | null] | null>
      key: string
    }
   ) | (
    Omit<DateFilterComponentProps, 'value' | 'classes' | 'color'>
    & {
      type: 'Date'
      value: Date
      // itemKey: TypedKeys<T, Date>
      key: string
    }
   )
)

export type InlineFilterPanelGridItem<T extends object> = {
  key: string
  type: 'GridItem'
  children: InlineFilterComponentConfigItem<T>[]
  gridSpacing?: GridSpacing
} & GridProps

export type InlineFilterPanelConfigItem<T extends object> = (
  | InlineFilterComponentConfigItem<T>
  | InlineFilterPanelGridItem<T>
)
//   (
//     Omit<MultiOptionFilterComponentProps, 'value' | 'classes' | 'color'>
//     & {
//       type: 'MultiOptionGroup'
//       value: unknown[]
//       // itemKey: TypedKeys<T, Array<unknown>>
//       key: string
//     }
//    ) | (
//     Omit<SingleOptionFilterComponentProps, 'value' | 'classes' | 'color'>
//     & {
//       type: 'SingleOptionGroup'
//       value: unknown
//       // itemKey: TypedKeys<T, undefined | null | string | number | NonArrayObject>
//       key: string
//     }
//    ) | (
//     Omit<DateRangeFilterComponentProps, 'value' | 'classes' | 'color'>
//     & {
//       type: 'DateRange'
//       value: [Date, Date]
//       // itemKey: TypedKeys<T, [Date | null, Date | null] | null>
//       key: string
//     }
//    ) | (
//     Omit<DateFilterComponentProps, 'value' | 'classes' | 'color'>
//     & {
//       type: 'Date'
//       value: Date
//       // itemKey: TypedKeys<T, Date>
//       key: string
//     }
//    )
// )

export type InlineFilterPanelProps<T extends object> = {
  values: T
  config: InlineFilterPanelConfigItem<T>[]
  color?: 'primary' | 'secondary'
  GridProps?: GridProps
} & Omit<BoxProps, 'children' | 'color'>


const useStyles = makeAppStyles<{}, FilterComponentClassName>( theme => ({
  editButton: {
    marginLeft: 'auto',
    alignSelf: 'flex-start',
    minWidth: 'fit-content',
  },
  dialogContentDividers: {
    padding: theme.spacing(3),
  },
  filterContainer: {
    display: 'flex',
    marginRight: theme.spacing(3),
  },
  filterDisplay: {
    alignSelf: 'flex-start',
    marginRight: theme.spacing(2),
  },
  filterLabel: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    fontSize: 12,
  },
  filterContent: {
    lineHeight: '32px',
  },
  filterButton: {
    height: 24,
    padding: 8,
  },
  filterChip: {
    height: 20,
    padding: 2,
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    '&:last-of-type': {
      marginRight: 0,
    }
  },
  filterContextMenuPopover: {
    marginLeft: theme.spacing(-0.5),
    marginRight: theme.spacing(0.5),
  },
}))


function InlineFilterPanelItem<T extends object>({
  color,
  classes,
  panelItem,
  gridSpacing,
}: {
  panelItem: InlineFilterPanelConfigItem<T>
  color: 'primary' | 'secondary'
  classes: ClassNameMap<FilterComponentClassName>
  gridSpacing: GridSpacing
}): JSX.Element | null {
  switch(panelItem.type){
    case 'Date':
      return (
        <Grid item xs='auto' key={panelItem.key}>
          <DateFilterComponent
            // key={String(itemKey)}
            classes={classes}
            color={color}
            {...panelItem} />
        </Grid>
      )
    case 'DateRange':
      return (
        <Grid item xs='auto' key={panelItem.key}>
          <DateRangeFilterComponent
            // key={String(itemKey)}
            classes={classes}
            color={color}
            {...panelItem} />
        </Grid>
      )
    case 'MultiOptionGroup':
      return (
        <Grid item xs='auto' key={panelItem.key}>
          <MultiOptionGroupFilterComponent
            // key={String(itemKey)}
            classes={classes}
            color={color}
            {...panelItem} />
        </Grid>
      )
    case 'SingleOptionGroup':
      return (
        <Grid item xs='auto' key={panelItem.key}>
          <SingleOptionGroupFilterComponent
            // key={String(itemKey)}
            classes={classes}
            color={color}
            {...panelItem} />
        </Grid>
      )
    case 'GridItem': {
      const { children, type, ...gridProps } = panelItem
      return (
        <Grid item {...gridProps}>
          <Grid container xs={12} spacing={gridSpacing}>
            { children.map( panelItem => (
              <InlineFilterPanelItem
                key={panelItem.key}
                color={color}
                classes={classes}
                gridSpacing={gridSpacing}
                panelItem={panelItem} />
            ))}
          </Grid>
        </Grid>
      )
    }
    default:
      return null
  }
}


export function InlineFilterPanel<T extends object>({
  values,
  config,
  color = 'primary',
  GridProps,
  ...props
}: InlineFilterPanelProps<T>): JSX.Element {

  const classes = useStyles()

  return (
    <Box display='flex' alignItems='center' {...props}>
      <Grid container spacing={3} alignItems='center' {...GridProps}>
        { config.map( (configItem) => (
          <InlineFilterPanelItem 
            key={configItem.key}
            color={color}
            classes={classes}
            gridSpacing={GridProps && GridProps.spacing || 3}
            panelItem={configItem} />
        ))}
      </Grid>
    </Box>
  )
}
